import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation
} from '@angular/core';
import { BaseComponent } from 'src/app/_core/tools/base.component';
import { Overlay } from '@angular/cdk/overlay';
import { LayoutService } from 'src/app/_core/services/layout.service';
import { ArticleMagazineModel, MutationGetRecommendationArticleMagazineArgs } from 'src/graphql/generated-types';
import { switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import gql from 'graphql-tag';
import { KalDialogConfig, KalDialogRef, KalDialogService } from '@kalidea/kaligraphi';
import {
    SharedMagazineDialogComponent,
    SharedMagazineDialogData,
    SharedMagazineDisplayMode,
    SharedMagazinePictureFormat
} from 'src/app/_shared/dialogs/shared-magazine-dialog/shared-magazine-dialog.component';
import { IGNORE_ALL_GRAPHQL_ERRORS } from 'src/app/_core/graphql.module';
import {
    MagazineActionEnum,
    MagazineActionType,
    MagazineService
} from 'src/app/_shared/services/magazine/magazine.service';

@Component({
    selector: 'app-shared-magazine',
    templateUrl: './shared-magazine.component.html',
    styleUrls: ['./shared-magazine.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SharedMagazineComponent extends BaseComponent implements OnInit, OnDestroy {

    magazine: ArticleMagazineModel = null;

    sharedMagazinePictureFormat = SharedMagazinePictureFormat;

    private dialogRef: KalDialogRef<SharedMagazineDialogComponent, SharedMagazineDialogData>;

    constructor(private readonly layoutService: LayoutService,
                private readonly overlay: Overlay,
                protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly dialogService: KalDialogService,
                private readonly magazineService: MagazineService) {
        super(cdr);
    }

    get isLarge(): boolean {
        return this.magazine.Mode === SharedMagazineDisplayMode.Large;
    }

    getPrimaryPicture(type: SharedMagazinePictureFormat): (string | null) {
        return this.magazine.ListeFormatImageMiseEnAvant?.KeyValueOfstringstring?.find(({Key}) => Key === type)?.Value || null;
    }

    openMagazineDialog(): void {
        const config = new KalDialogConfig<SharedMagazineDialogData>({
            panelClass: ['dialog-magazine', 'dialog-magazine-' + this.magazine.Mode.toLowerCase()],
            data: {
                magazine: this.magazine
            },
            maxWidth: this.isLarge ? '100vw' : '80vw',
            backdropClass: 'kal-layout__navigation__submenu-backdrop',
            positionStrategy: this.overlay.position()
                .flexibleConnectedTo(this.layoutService.navigationRefElement)
                .withPositions([
                    {originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'center'}
                ])
        });

        if (this.isLarge) {
            config.hasBackdrop = false;
        }

        this.watch$ = this.magazineService.magazineAction(this.magazine, MagazineActionEnum.Detail as MagazineActionType);

        this.dialogRef = this.dialogService.open(SharedMagazineDialogComponent, config);
        this.watch$ = this.dialogRef.afterClosed.pipe(
            switchMap(() => this.magazineService.magazineAction(this.magazine, MagazineActionEnum.Close as MagazineActionType)),
        );
    }


    private getMagazine(): void {
        this.watch$ = this.graphqlService.mutateOne<'GetRecommendationArticleMagazine', MutationGetRecommendationArticleMagazineArgs>({
            mutation: gql`
                mutation GetRecommendationArticleMagazine(
                    $contexte: ContextInfosInput,
                ) {
                    GetRecommendationArticleMagazine(
                        contexte: $contexte
                        criteres: {
                            ApplicationCode: "VUP"
                            IsRandom: false
                            Take: 1
                        }
                    ) {
                        ArticleMagazineModel {
                            Id
                            Titre
                            Summary
                            Contenu
                            Mode
                            ListeFormatImageMiseEnAvant {
                                KeyValueOfstringstring {
                                    Key
                                    Value
                                }
                            }
                            ListeFormatImageAttachee {
                                KeyValueOfstringstring {
                                    Key
                                    Value
                                }
                            }
                            NombreLikes
                            CanLike
                        }
                    }
                }
            `,
            context: {
                [IGNORE_ALL_GRAPHQL_ERRORS]: true
            }
        }).pipe(
            tap(GetRecommendationArticleMagazine => {
                this.magazine = GetRecommendationArticleMagazine?.ArticleMagazineModel?.[0];
                this.cdr.markForCheck();
            })
        );
    }

    ngOnDestroy(): void {
        if (this.dialogRef) {
            this.dialogRef.close();
        }

        super.ngOnDestroy();
    }

    ngOnInit(): void {
        this.getMagazine();
    }
}
